import { MediaImageStudioMode } from '@wix/media-image-studio-opener';
import { all, any, equals, isEmpty, length, none, pipe, pluck, prop, uniq } from 'ramda';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { ContextMenu } from 'react-contextmenu';
import { FormattedMessage } from 'react-intl';
import { SubMenu, WixIcons } from '../../../components/common';
import { BoardsContextMenu } from '../../../components/common/BoardsContextMenu';
import { BoardsContextSubMenuContent } from '../../../components/common/BoardsContextMenu/BoardsContextMenu';
import { ContextMenuItem } from '../../../components/common/ContextMenu/ContextMenuItem';
import { IContextMenuItemParams } from '../../../components/common/ContextMenu/ContextMenuItem/ContextMenuItem.types';
import { ActionName, BiAddMediaClickOrigin } from '../../../constants/Bi';
import { ContextMenuId } from '../../../constants/ContextMenuId';
import { Layout } from '../../../constants/Layout';
import { ModalName } from '../../../constants/ModalName';
import { Sort } from '../../../constants/Sort';
import { ClickOrigin } from '../../../utils/hoc/bi/withTrackSiteFilesAnalytics/withTrackSiteFilesAnalytics.types';
import { useMMGRSettings } from '../../../utils/hoc/withMMGRSettings';
import { useTrackActions } from '../../../utils/hooks/bi/useTrackActions';
import { ActionTarget } from '../../../utils/hooks/bi/useTrackActions/useTrackActions.types';
import { useTrackAddMediaButtonClickAnalytics } from '../../../utils/hooks/bi/useTrackAddMediaButtonClickAnalytics';
import {
  TrackBoardActionsMenuTarget,
  TrackBoardClickOrigin,
  useTrackBoardAnalytics,
} from '../../../utils/hooks/bi/useTrackBoardAnalytics';
import { LayoutChangeClickOrigin, useTrackLayoutChange } from '../../../utils/hooks/bi/useTrackLayoutChange';
import { useTrackPreviewActions } from '../../../utils/hooks/bi/useTrackPreviewActions';
import { useTrackTrashActions } from '../../../utils/hooks/bi/useTrackTrashActions';
import { useBoards } from '../../../utils/hooks/useBoards';
import { useBoolean } from '../../../utils/hooks/useBoolean';
import { useMediaStudio } from '../../../utils/hooks/useMediaStudio';
import { useAdobeExpress } from '../../../utils/hooks/useAdobeExpress/useAdobeExpress';
import { useModalHandlersAndOptions } from '../../../utils/hooks/useModal';
import { useStorageQuotaAwareHandler } from '../../../utils/hooks/storageQuota/useStorageQuotaAwareHandler';
import { StorageQuotaUseCase } from '../../../utils/hooks/storageQuota/useStorageQuotaAwareHandler/StorageQuotaUseCase';
import { usePermanentDeleteModalHandlers } from '../../../utils/hooks/useModal/modals/usePermanentDeleteModal';
import { useSources } from '../../../utils/hooks/useSources';
import { useCanUseItemsInVideoMaker, useVideoMaker } from '../../../utils/hooks/useVideoMaker/useVideoMaker';
import { MediaManagerSDKAnyItem, MediaManagerSDKBoard, MediaManagerSDKFile } from '../../../utils/media-manager-sdk';
import { isBoard, isSystemBoard, isUserOwnerOfBoard } from '../../../utils/media-manager-sdk/boards';
import {
  canCopyLinkBeProvided,
  canCreateNewSubfolder,
  canEditItemInVideoMaker,
  canItemBeAddedToFavorite,
  canItemBeExpanded,
  canItemsBeShared,
  canOpenItemInAdobeExpress,
  canOpenItemInImageEditor,
  canPasteToItem,
  isFolder,
  isItemCopyable,
  isItemCuttable,
  isItemDownloadable,
  isItemMovable,
  isItemRemovable,
  isItemRenamable,
  isRootFolder,
  isSystemFolder,
  sameBy,
} from '../../../utils/media-manager-sdk/items';
import { isTranscoding } from '../../../utils/media-manager-sdk/transcoding';
import styles from './ContextMenus.scss';
import { IOuter } from './ContextMenus.types';
import { AdobeOpenClickSource, useAdobeExpressBI } from '../../../utils/hooks/useAdobeExpress/useAdobeExpressBI';
import { useFileAwarenessModalHandlers } from '../../../utils/hooks/useModal/modals/useFileAwarenessModal';
import { HttpClient } from '@wix/http-client';
import { getInstance } from '../../../utils/getInstance';
import { parseSiteMediaToken } from '../../../utils/siteMediaToken';

export const ContextMenus: FC<IOuter> = ({
  pasteItems,
  hasCutItems,
  hasCopiedItems,
  createNewFolderStart,
  selectedItems,
  currentFolder,
  source,
  layout,
  layouts,
  setLayout,
  setSort,
  removeSelectedItemsFromBoard,
  handleEditItemStart,
  isInExpandedMode,
  openExpandedView,
  copyFileUrlToClipboard,
  copyWixUrlToClipboard,
  copySelectedItems,
  cutSelectedItems,
  restoreItems,
  moveItemsToTrash,
  startDownloadItem,
  onDownloadSelectedItems,
}) => {
  const { mmgrSettings } = useMMGRSettings();
  const {
    isSecureContext,
    isLinkToPublicCMSExperimentEnabled,
    publicCMSDomain,
    team,
    isFileAwarenessEnabled,
    isHideAdobeExpressEnabled,
    isCopyWixUrlEnabled,
    metaSiteToken,
    hideCreateAndEditActions,
  } = mmgrSettings;

  const httpClient = useMemo(async () => {
    const instanceToken =
      metaSiteToken || (await getInstance(parseSiteMediaToken(mmgrSettings.siteMediaToken).metaSiteId));
    return new HttpClient({
      getAppToken: () => instanceToken,
    });
  }, [metaSiteToken, mmgrSettings.siteMediaToken]);

  const storageQuotaAware = useStorageQuotaAwareHandler();

  const { openModal: openMoveToModal } = useModalHandlersAndOptions(ModalName.MoveTo);
  const { openModal: openEditBoardModal } = useModalHandlersAndOptions(ModalName.EditBoard);
  const { openModal: openAddFilesToBoardModal } = useModalHandlersAndOptions(ModalName.AddFilesToBoard);
  const { openModal: openRemoveFilesModal } = usePermanentDeleteModalHandlers();
  const { openModal: openFileAwarenessModal } = useFileAwarenessModalHandlers();
  const { openModal: openDeleteBoardModal } = useModalHandlersAndOptions(ModalName.DeleteBoard);
  const openFilesImporterModal = storageQuotaAware(
    useModalHandlersAndOptions(ModalName.FilesImporter).openModal,
    StorageQuotaUseCase.UPLOAD,
  );

  const { openMediaStudio } = useMediaStudio();
  const { editImageInAdobe } = useAdobeExpress(currentFolder);
  const { openVideoMaker } = useVideoMaker();
  const { isTrash, isSiteFiles, isFavorites, isHome, isBoards, isBoardsRoot, isSearch, isMediaFromWix } = useSources({
    source,
  });
  const canDisplayEditUnsplashAction = selectedItems.length === 1 && canOpenItemInImageEditor(selectedItems[0]);
  const canUseItemsInVideoMaker = useCanUseItemsInVideoMaker(selectedItems);

  const { trackPreviewActionsContextMenuClicked } = useTrackPreviewActions();
  const { trackRestore, trackMoveToTrash } = useTrackTrashActions();
  const { trackAddMediaButtonClickContextMenu } = useTrackAddMediaButtonClickAnalytics();
  const { trackActionsMenuClick: _trackActionsMenuClick } = useTrackActions();
  const { trackBoardActionsClick, trackCreateBoardClick } = useTrackBoardAnalytics();
  const { trackLayoutChange, trackLayoutClick } = useTrackLayoutChange();

  const areItemsSelectedInBoard = isBoards && !isBoard(selectedItems[0]);

  const {
    isSystemBoardSelected,
    userIsOwnerOfSelectedBoards,
    hasBoardInSelectedItems,
    hasSelectedItems,
    isSingleItemSelected,
    areAllItemsFromOneSource,
    canEditVideoFromVideoMaker,
    canEditInAdobeExpress,
    canItemsBeExpanded,
    areItemsMovable,
    areItemsCopyable,
    areItemsCuttable,
    canPasteToItems,
    noSystemFoldersSelected,
    isSingleFolderSelected,
    canCopyLinkForItems,
    canItemsBeDownloaded,
    areItemsRemovable,
  } = useMemo(() => {
    const isSingleItemSelected = selectedItems.length === 1;
    const hasBoardInSelectedItems = isBoard(selectedItems[0]);

    return {
      isSystemBoardSelected: any(isSystemBoard, selectedItems),
      userIsOwnerOfSelectedBoards: hasBoardInSelectedItems && all(isUserOwnerOfBoard)(selectedItems),
      hasBoardInSelectedItems,
      isSingleItemSelected,
      hasSelectedItems: !isEmpty(selectedItems),
      areAllItemsFromOneSource: sameBy(prop('source'), selectedItems),
      canEditVideoFromVideoMaker: canEditItemInVideoMaker(selectedItems),
      canEditInAdobeExpress:
        isSingleItemSelected && canOpenItemInAdobeExpress(isHideAdobeExpressEnabled)(selectedItems[0]),
      canItemsBeExpanded: all(canItemBeExpanded, selectedItems),
      canItemsBeAddedToFavorite: all(canItemBeAddedToFavorite, selectedItems),
      areItemsMovable: all(isItemMovable, selectedItems) && !areItemsSelectedInBoard,
      areItemsCopyable: all(isItemCopyable, selectedItems) && !areItemsSelectedInBoard,
      areItemsCuttable: all(isItemCuttable, selectedItems) && !areItemsSelectedInBoard,
      canPasteToItems: all(canPasteToItem, selectedItems) && !areItemsSelectedInBoard,
      canCopyLinkForItems: all(canCopyLinkBeProvided, selectedItems) && !areItemsSelectedInBoard,
      noSystemFoldersSelected: none(isSystemFolder, selectedItems),
      isSingleFolderSelected: isSingleItemSelected && isFolder(selectedItems[0]),
      canItemsBeDownloaded: all(isItemDownloadable, selectedItems),
      areItemsRemovable: all(isItemRemovable, selectedItems) && !areItemsSelectedInBoard,
    };
  }, [isHideAdobeExpressEnabled, selectedItems, areItemsSelectedInBoard]);

  const shouldDisableEditActions =
    (isSingleItemSelected && isTranscoding(selectedItems[0])) || hideCreateAndEditActions;

  const trackActionsMenuClick = useCallback(
    (params: IContextMenuItemParams, actionName, target?) => {
      _trackActionsMenuClick(
        hasSelectedItems ? selectedItems : [currentFolder],
        params.contextMenuOrigin,
        actionName,
        target,
      );
    },
    [_trackActionsMenuClick, hasSelectedItems, selectedItems, currentFolder],
  );

  const handleCreateNewFolderClick = useCallback(
    (_event, params) => {
      createNewFolderStart(ClickOrigin.Actions);
      trackActionsMenuClick(params, ActionName.CreateFolder, ActionTarget.Empty);
    },
    [createNewFolderStart, trackActionsMenuClick],
  );

  const handlePasteClick = useCallback(
    (_event, params) => {
      pasteItems();
      trackActionsMenuClick(params, ActionName.Paste, ActionTarget.Empty);
    },
    [pasteItems, trackActionsMenuClick],
  );

  const handleAddFilesToFolderClick = useCallback(
    (_event, params) => {
      openFilesImporterModal({ openedFrom: BiAddMediaClickOrigin.ContextMenu });
      trackAddMediaButtonClickContextMenu();
      trackActionsMenuClick(params, ActionName.AddFilesToFolder, ActionTarget.Empty);
    },
    [openFilesImporterModal, trackAddMediaButtonClickContextMenu, trackActionsMenuClick],
  );

  const handleEmptyTrashClick = useCallback(
    (_event, params) => {
      if (isFileAwarenessEnabled) {
        openFileAwarenessModal({ clickOrigin: params.contextMenuOrigin, selectedItems });
      } else {
        openRemoveFilesModal({ clickOrigin: params.contextMenuOrigin });
      }
      trackActionsMenuClick(params, ActionName.EmptyTrash, ActionTarget.Empty);
    },
    [isFileAwarenessEnabled, openFileAwarenessModal, openRemoveFilesModal, trackActionsMenuClick],
  );

  const handleLayoutChange = useCallback(
    (newLayout, params) => {
      trackLayoutClick(LayoutChangeClickOrigin.Actions);
      trackLayoutChange(layout, newLayout);
      trackActionsMenuClick(params, ActionName.LayoutChange, ActionTarget.Empty);
      setLayout(newLayout);
    },
    [setLayout, trackLayoutChange, trackLayoutClick, trackActionsMenuClick, layout],
  );
  const handleSwitchToGalleryLayout = useCallback(
    (_event, params) => handleLayoutChange(Layout.Gallery, params),
    [handleLayoutChange],
  );
  const handleSwitchToListLayout = useCallback(
    (_event, params) => handleLayoutChange(Layout.List, params),
    [handleLayoutChange],
  );

  const handleSortChange = useCallback(
    (newSort, params) => {
      setSort(newSort);
      trackActionsMenuClick(params, ActionName.SortChange, ActionTarget.Empty);
    },
    [setSort, trackActionsMenuClick],
  );
  const handleSwitchToAZSort = useCallback((_event, params) => handleSortChange(Sort.AZ, params), [handleSortChange]);
  const handleSwitchToZASort = useCallback((_event, params) => handleSortChange(Sort.ZA, params), [handleSortChange]);
  const handleSwitchToLastModifiedSort = useCallback(
    (_event, params) => handleSortChange(Sort.LastModified, params),
    [handleSortChange],
  );
  const handleSwitchToOldestFirstSort = useCallback(
    (_event, params) => handleSortChange(Sort.OldestFirst, params),
    [handleSortChange],
  );

  const handleEditBoardClick = useCallback(
    (_event, params) => {
      const clickOrigin = params.contextMenuOrigin;

      openEditBoardModal({ board: selectedItems[0] || currentFolder, clickOrigin });

      trackBoardActionsClick(selectedItems as MediaManagerSDKBoard[], {
        target: TrackBoardActionsMenuTarget.Edit,
        clickOrigin,
      });
    },
    [selectedItems, currentFolder, openEditBoardModal, trackBoardActionsClick],
  );

  const handleCreateBoardClick = useCallback(
    (_event, params) => {
      const clickOrigin = params.contextMenuOrigin;
      openEditBoardModal({ clickOrigin });

      trackBoardActionsClick(selectedItems as MediaManagerSDKBoard[], {
        target: TrackBoardActionsMenuTarget.Create,
        clickOrigin,
      });
      trackCreateBoardClick(TrackBoardClickOrigin.ItemContextMenu);
    },
    [openEditBoardModal, trackBoardActionsClick, selectedItems, trackCreateBoardClick],
  );

  const handleAddFilesToBoardsClick = useCallback(
    (_event, params) => {
      const options = hasSelectedItems
        ? { boards: selectedItems }
        : { boards: [currentFolder], shouldUpdateItemsInFolder: true };
      const { contextMenuOrigin: clickOrigin } = params;

      openAddFilesToBoardModal({ ...options, clickOrigin });

      trackBoardActionsClick(selectedItems as MediaManagerSDKBoard[], {
        target: TrackBoardActionsMenuTarget.AddFiles,
        clickOrigin,
      });
    },
    [hasSelectedItems, selectedItems, currentFolder, openAddFilesToBoardModal, trackBoardActionsClick],
  );

  const handleDeleteBoardClick = useCallback(
    (_event, params) => {
      openDeleteBoardModal({ boards: selectedItems });

      trackBoardActionsClick(selectedItems as MediaManagerSDKBoard[], {
        target: TrackBoardActionsMenuTarget.Delete,
        clickOrigin: params.contextMenuOrigin,
      });
    },
    [selectedItems, openDeleteBoardModal, trackBoardActionsClick],
  );

  const { setItemsToAdd } = useBoards();

  const handleRemoveFromBoardClick = useCallback(
    (_event, params) => {
      const clickOrigin = params.contextMenuOrigin;

      removeSelectedItemsFromBoard({ clickOrigin });

      trackActionsMenuClick(params, ActionName.RemoveFromBoard);
    },
    [removeSelectedItemsFromBoard, trackActionsMenuClick],
  );

  const contextMenuItemRemoveFromBoard = isBoard(currentFolder) && (
    <ContextMenuItem
      title={<FormattedMessage id="actions.remove-from-board.context-menu" />}
      onClick={handleRemoveFromBoardClick}
      dataHook="context-menu.remove-from-board"
    />
  );

  const handleRenameClick = useCallback(
    (_event, params) => {
      handleEditItemStart();
      trackActionsMenuClick(params, ActionName.Rename);
    },
    [handleEditItemStart, trackActionsMenuClick],
  );

  const contextMenuItemRename = !isSearch && !isHome && isSingleItemSelected && all(isItemRenamable, selectedItems) && (
    <ContextMenuItem
      title={<FormattedMessage id="actions.rename" />}
      onClick={handleRenameClick}
      dataHook="context-menu.rename"
    />
  );

  const handleCropEditClick = useCallback(
    (_event, params) => {
      openMediaStudio({ file: selectedItems[0] as MediaManagerSDKFile, mode: MediaImageStudioMode.Transform });
      trackActionsMenuClick(params, ActionName.CropEdit);
    },
    [openMediaStudio, trackActionsMenuClick, selectedItems],
  );

  const contextMenuItemCropEdit = !isHome &&
    isSingleItemSelected &&
    !shouldDisableEditActions &&
    (all(canOpenItemInImageEditor, selectedItems) || canDisplayEditUnsplashAction) && (
      <ContextMenuItem
        title={<FormattedMessage id="actions.crop-and-edit" />}
        onClick={handleCropEditClick}
        dataHook="context-menu.crop-edit"
      />
    );

  const handleCutOutClick = useCallback(
    (_event, params) => {
      openMediaStudio({ file: selectedItems[0] as MediaManagerSDKFile, mode: MediaImageStudioMode.Cutout });
      trackActionsMenuClick(params, ActionName.ChangeBackground);
    },
    [openMediaStudio, selectedItems, trackActionsMenuClick],
  );

  const contextMenuItemChangeBackground = !isHome &&
    isSingleItemSelected &&
    !shouldDisableEditActions &&
    all(canOpenItemInImageEditor, selectedItems) && (
      <ContextMenuItem
        title={<FormattedMessage id="actions.image-background" />}
        onClick={handleCutOutClick}
        dataHook="context-menu.image-background"
      />
    );

  const { trackAdobeOpenClick } = useAdobeExpressBI();
  const handleEditInAdobeClick = useCallback(
    (_event, params) => {
      editImageInAdobe(selectedItems[0] as MediaManagerSDKFile);
      trackActionsMenuClick(params, ActionName.EditInAdobeExpress);
      trackAdobeOpenClick(selectedItems[0] as MediaManagerSDKFile, AdobeOpenClickSource.ActionsMenu);
    },
    [editImageInAdobe, selectedItems, trackActionsMenuClick, trackAdobeOpenClick],
  );

  const contextMenuItemEditInAdobe = !isHome && canEditInAdobeExpress && (
    <ContextMenuItem
      title={<FormattedMessage id="actions.image.adobe-express" />}
      onClick={handleEditInAdobeClick}
      dataHook="context-menu.adobe"
    />
  );

  const handleEditInVideoMakerClick = useCallback(
    (_event, params) => {
      openVideoMaker(selectedItems as MediaManagerSDKFile[]);
      trackActionsMenuClick(params, canEditVideoFromVideoMaker ? ActionName.ReEdit : ActionName.CreateVideo);
    },
    [canEditVideoFromVideoMaker, openVideoMaker, selectedItems, trackActionsMenuClick],
  );

  const contextMenuItemCreateVideo = !isBoards && !isHome && canUseItemsInVideoMaker && !shouldDisableEditActions && (
    <ContextMenuItem
      title={
        canEditVideoFromVideoMaker ? (
          <FormattedMessage id="actions.edit-video" />
        ) : (
          <FormattedMessage id="actions.create-video" />
        )
      }
      onClick={handleEditInVideoMakerClick}
      dataHook={canEditVideoFromVideoMaker ? 'context-menu.edit-video' : 'context-menu.create-new-video'}
    />
  );

  const isNewLayoutFolder = !isRootFolder(currentFolder) && !hasSelectedItems;

  const handleExpandClick = useCallback(
    (_event, params) => {
      openExpandedView();
      trackPreviewActionsContextMenuClicked(selectedItems);
      trackActionsMenuClick(params, ActionName.Preview);
    },
    [openExpandedView, trackPreviewActionsContextMenuClicked, selectedItems, trackActionsMenuClick],
  );

  const contextMenuItemExpand = !isInExpandedMode && canItemsBeExpanded && !isNewLayoutFolder && (
    <ContextMenuItem
      title={<FormattedMessage id="actions.expand" />}
      onClick={handleExpandClick}
      hotKey={<FormattedMessage id="actions.hotkey.space" />}
      dataHook="context-menu.preview"
    />
  );

  const handleMoveToClick = useCallback(
    (_event, params) => {
      openMoveToModal();
      trackActionsMenuClick(params, ActionName.MoveTo);
    },
    [openMoveToModal, trackActionsMenuClick],
  );

  const contextMenuItemMoveTo = !isHome && isSiteFiles && areItemsMovable && noSystemFoldersSelected && (
    <ContextMenuItem
      title={<FormattedMessage id="actions.move-to" />}
      onClick={handleMoveToClick}
      dataHook="context-menu.move-to"
    />
  );

  const handleCopyClick = useCallback(
    (_event, params) => {
      copySelectedItems();
      trackActionsMenuClick(params, ActionName.Copy);
    },
    [copySelectedItems, trackActionsMenuClick],
  );

  const contextMenuItemCopy = !isHome && areItemsCopyable && (
    <ContextMenuItem
      title={<FormattedMessage id="actions.copy" />}
      onClick={handleCopyClick}
      hotKey={<FormattedMessage id="actions.hotkey.ctrl-c" />}
      dataHook="context-menu.copy"
    />
  );

  const handleCutClick = useCallback(
    (_event, params) => {
      cutSelectedItems();
      trackActionsMenuClick(params, ActionName.Cut);
    },
    [cutSelectedItems, trackActionsMenuClick],
  );

  const contextMenuItemCut = !isBoards && !isHome && !isFavorites && areItemsCuttable && (
    <ContextMenuItem
      title={<FormattedMessage id="actions.cut" />}
      onClick={handleCutClick}
      hotKey={<FormattedMessage id="actions.hotkey.ctrl-x" />}
      dataHook="context-menu.cut"
    />
  );

  const handlePasteHereClick = useCallback(
    (_event, params) => {
      pasteItems();
      trackActionsMenuClick(params, ActionName.PasteHere);
    },
    [pasteItems, trackActionsMenuClick],
  );

  const contextMenuItemPasteHere = !isHome && !isFavorites && isSingleFolderSelected && canPasteToItems && (
    <ContextMenuItem
      title={<FormattedMessage id="actions.paste-here" />}
      onClick={handlePasteHereClick}
      disabled={!hasCutItems && !hasCopiedItems}
      dataHook="context-menu.paste-here"
    />
  );

  const handleCopyUrlClick = useCallback(
    (_event, params) => {
      copyFileUrlToClipboard();
      trackActionsMenuClick(params, ActionName.CopyUrl);
    },
    [copyFileUrlToClipboard, trackActionsMenuClick],
  );

  const contextMenuItemCopyUrlLink = !isHome && !isSecureContext && isSingleItemSelected && canCopyLinkForItems && (
    <ContextMenuItem
      title={<FormattedMessage id="actions.copy-url-link" />}
      onClick={handleCopyUrlClick}
      dataHook="context-menu.copy-url-link"
    />
  );

  const handleCopyWixUrlClick = useCallback(
    async (_event, params) => {
      copyWixUrlToClipboard(await httpClient);
      trackActionsMenuClick(params, ActionName.CopyWixUrl);
    },
    [copyWixUrlToClipboard, trackActionsMenuClick, httpClient],
  );

  const contextMenuItemCopyWixUrl = !isHome &&
    !isSecureContext &&
    isSingleItemSelected &&
    canCopyLinkForItems &&
    isCopyWixUrlEnabled && (
      <ContextMenuItem
        title={<FormattedMessage id="actions.copy-wix-url" />}
        onClick={handleCopyWixUrlClick}
        dataHook="context-menu.copy-wix-url"
      />
    );

  const handleDownloadClick = useCallback(
    (_event, params) => {
      startDownloadItem(selectedItems[0] as MediaManagerSDKFile);
      trackActionsMenuClick(params, ActionName.Download);
    },
    [startDownloadItem, trackActionsMenuClick, selectedItems],
  );

  const handleEditCMSClick = useCallback(() => {
    const url = `${publicCMSDomain}/cms/index.html#/item/${selectedItems[0].id}`;
    window.open(url, '_blank');
  }, [publicCMSDomain, selectedItems]);

  const canDownloadSingle = !isHome && isSingleItemSelected;

  const areSelectedFilesFromSameSite =
    canItemsBeDownloaded &&
    pipe<MediaManagerSDKAnyItem[], string[], string[], number, boolean>(
      pluck('siteId'),
      uniq,
      length,
      equals(1),
    )(selectedItems);

  const canDownloadMultiple = areSelectedFilesFromSameSite && !isSecureContext && selectedItems.length > 1;

  const canEditInPublicCMS = isSingleItemSelected && isMediaFromWix && isLinkToPublicCMSExperimentEnabled;

  const contextMenuEditInPublicCMS = canEditInPublicCMS && (
    <ContextMenuItem
      title={<FormattedMessage id="actions.edit-cms" />}
      dataHook="context-menu.edit-cms"
      onClick={handleEditCMSClick}
    />
  );

  const contextMenuItemDownload = !isHome && canItemsBeDownloaded && (canDownloadSingle || canDownloadMultiple) && (
    <ContextMenuItem
      title={<FormattedMessage id="actions.download" />}
      onClick={isSingleItemSelected ? handleDownloadClick : onDownloadSelectedItems}
      dataHook="context-menu.download"
    />
  );

  const handleRestoreClick = useCallback(
    (_event, params) => {
      trackRestore(selectedItems, { clickOrigin: params.contextMenuOrigin });
      restoreItems();
      trackActionsMenuClick(params, ActionName.Restore);
    },
    [trackRestore, selectedItems, restoreItems, trackActionsMenuClick],
  );

  const contextMenuItemRestore = isTrash && (
    <ContextMenuItem
      title={<FormattedMessage id="actions.restore" />}
      onClick={handleRestoreClick}
      dataHook="context-menu.restore"
    />
  );

  const handleDeleteForeverClick = useCallback(
    (_event, params) => {
      if (isFileAwarenessEnabled) {
        openFileAwarenessModal({ clickOrigin: params.contextMenuOrigin, selectedItems });
      } else {
        openRemoveFilesModal({ clickOrigin: params.contextMenuOrigin });
      }
      trackActionsMenuClick(params, ActionName.DeleteForever);
    },
    [isFileAwarenessEnabled, openFileAwarenessModal, openRemoveFilesModal, trackActionsMenuClick],
  );

  const contextMenuItemDeleteForever = isTrash && (
    <ContextMenuItem
      title={<FormattedMessage id="actions.delete-forever" />}
      onClick={handleDeleteForeverClick}
      dataHook="context-menu.delete-forever"
    />
  );

  const handleMoveToTrashClick = useCallback(
    (_event, params) => {
      trackMoveToTrash(selectedItems, { clickOrigin: params.contextMenuOrigin });
      trackActionsMenuClick(params, ActionName.MoveToTrash);
      moveItemsToTrash(selectedItems);
    },
    [trackMoveToTrash, selectedItems, trackActionsMenuClick, moveItemsToTrash],
  );

  const contextMenuItemMoveToTrash = !isBoards && !isHome && areItemsRemovable && (
    <ContextMenuItem
      title={<FormattedMessage id="actions.move-to-trash" />}
      onClick={handleMoveToTrashClick}
      dataHook="context-menu.move-to-trash"
    />
  );

  const hasCutCopyPasteRelatedActions = any(Boolean, [
    contextMenuItemMoveTo,
    contextMenuItemCopy,
    contextMenuItemCut,
    contextMenuItemPasteHere,
    contextMenuItemCopyUrlLink,
    contextMenuItemDownload,
  ]);

  const hasTrashRelatedActions = any(Boolean, [
    contextMenuItemMoveToTrash,
    contextMenuItemRestore,
    contextMenuItemDeleteForever,
  ]);

  const selectedBoardsContextMenuItems = hasBoardInSelectedItems && (
    <>
      <ContextMenuItem
        title={
          <FormattedMessage
            id="boards.context-menu.add-files-to-board"
            values={{ boardsCount: selectedItems.length }}
          />
        }
        dataHook="context-menu.add-files-to-board"
        onClick={handleAddFilesToBoardsClick}
      />

      {!isSystemBoardSelected && userIsOwnerOfSelectedBoards && (
        <>
          <ContextMenuItem divider />

          {isSingleItemSelected && (
            <ContextMenuItem
              title={<FormattedMessage id="boards.context-menu.edit-board" />}
              dataHook="context-menu.edit-board"
              onClick={handleEditBoardClick}
            />
          )}

          <ContextMenuItem
            title={
              <FormattedMessage id="boards.context-menu.delete-board" values={{ boardsCount: selectedItems.length }} />
            }
            dataHook="context-menu.delete-board"
            onClick={handleDeleteBoardClick}
          />
        </>
      )}
    </>
  );

  const currentBoardContextMenuItems = isBoard(currentFolder) && (
    <>
      {!isSystemBoard(currentFolder) && (
        <>
          <ContextMenuItem
            title={<FormattedMessage id="boards.context-menu.edit-board" />}
            dataHook="context-menu.edit-board"
            onClick={handleEditBoardClick}
          />
          <ContextMenuItem divider />
        </>
      )}

      <ContextMenuItem
        title={
          <FormattedMessage
            id="boards.context-menu.add-files-to-board"
            values={{ boardsCount: selectedItems.length }}
          />
        }
        dataHook="context-menu.add-files-to-board"
        onClick={handleAddFilesToBoardsClick}
      />
    </>
  );

  const itemsToAddToBoard = useMemo(
    () => (selectedItems.length ? selectedItems : [currentFolder]),
    [selectedItems, currentFolder],
  );

  const { value: isContextMenuOpen, setTrue: setContextMenuOpened, setFalse: setContextMenuClosed } = useBoolean(false);

  useEffect(() => {
    if (isContextMenuOpen) {
      setItemsToAdd(itemsToAddToBoard);
    }
  }, [isContextMenuOpen, setItemsToAdd, itemsToAddToBoard]);

  const handleContextMenuShow = useCallback(() => {
    setContextMenuOpened();
  }, [setContextMenuOpened]);

  const handleContextMenuHide = useCallback(() => {
    setContextMenuClosed();
  }, [setContextMenuClosed]);

  const shouldShowAddtoBoards = !isTrash && canItemsBeShared(itemsToAddToBoard, team);

  return (
    <>
      <ContextMenu
        id={ContextMenuId.Boards}
        className={styles.boardsContextMenu}
        onShow={handleContextMenuShow}
        onHide={handleContextMenuHide}
      >
        <BoardsContextMenu itemsToAdd={itemsToAddToBoard} />
      </ContextMenu>

      <ContextMenu id={ContextMenuId.SelectedItems} onShow={handleContextMenuShow} onHide={handleContextMenuHide}>
        {selectedBoardsContextMenuItems ? (
          selectedBoardsContextMenuItems
        ) : areAllItemsFromOneSource ? (
          <>
            {shouldShowAddtoBoards && (
              <>
                <BoardsContextSubMenuContent itemsToAdd={itemsToAddToBoard} />
                {contextMenuItemRemoveFromBoard}
                <ContextMenuItem divider />
              </>
            )}
            {contextMenuEditInPublicCMS}
            {contextMenuItemRename}
            {contextMenuItemCropEdit}
            {contextMenuItemCreateVideo}
            {contextMenuItemChangeBackground}
            {contextMenuItemEditInAdobe}
            {contextMenuItemExpand}
            {hasCutCopyPasteRelatedActions && (
              <>
                <ContextMenuItem divider />
                {contextMenuItemMoveTo}
                {contextMenuItemCopy}
                {contextMenuItemCut}
                {contextMenuItemPasteHere}
                {contextMenuItemCopyUrlLink}
                {contextMenuItemCopyWixUrl}
                {contextMenuItemDownload}
              </>
            )}
            {hasTrashRelatedActions && (
              <>
                <ContextMenuItem divider />
                {contextMenuItemMoveToTrash}
                {contextMenuItemRestore}
                {contextMenuItemDeleteForever}
              </>
            )}
          </>
        ) : (
          contextMenuItemExpand
        )}
      </ContextMenu>

      <ContextMenu id={ContextMenuId.CurrentFolder}>
        {currentBoardContextMenuItems ? (
          currentBoardContextMenuItems
        ) : (
          <>
            {isBoardsRoot && (
              <>
                <ContextMenuItem
                  title={<FormattedMessage id="actions.create-new-board" />}
                  onClick={handleCreateBoardClick}
                  dataHook="context-menu.create-new-board"
                />
                <ContextMenuItem divider />
              </>
            )}

            {!isBoardsRoot && currentFolder && canCreateNewSubfolder(currentFolder) && (
              <>
                <ContextMenuItem
                  title={<FormattedMessage id="actions.create-new-folder" />}
                  onClick={handleCreateNewFolderClick}
                  dataHook="context-menu.create-new-folder"
                />
                <ContextMenuItem divider />
              </>
            )}

            {currentFolder && canPasteToItem(currentFolder) && (
              <ContextMenuItem
                title={<FormattedMessage id="actions.paste" />}
                onClick={handlePasteClick}
                disabled={!hasCutItems && !hasCopiedItems}
                hotKey={<FormattedMessage id="actions.hotkey.ctrl-v" />}
                dataHook="context-menu.paste"
              />
            )}

            {!isBoardsRoot && (
              <>
                <ContextMenuItem
                  title={<FormattedMessage id="actions.add-files-to-folder" />}
                  onClick={handleAddFilesToFolderClick}
                  dataHook="context-menu.add-files-to-folder"
                />
                <ContextMenuItem divider />
              </>
            )}

            {!isBoardsRoot && layouts.length > 1 && (
              <SubMenu title={<FormattedMessage id="actions.view" />} dataHook="context-menu.layout-switcher-sub-menu">
                <ContextMenuItem
                  title={<FormattedMessage id="layout-switcher.gallery" />}
                  icon={<WixIcons.LayoutGallerySmall />}
                  dataHook="context-menu.layout-change.gallery"
                  onClick={handleSwitchToGalleryLayout}
                />
                <ContextMenuItem
                  title={<FormattedMessage id="layout-switcher.list" />}
                  icon={<WixIcons.LayoutListSmall />}
                  dataHook="context-menu.layout-change.list"
                  onClick={handleSwitchToListLayout}
                />
              </SubMenu>
            )}

            <SubMenu title={<FormattedMessage id="actions.sort-by" />} dataHook="context-menu.sort-switcher-sub-menu">
              <ContextMenuItem
                title={<FormattedMessage id="sort-switcher.last-modified" />}
                icon={<WixIcons.SortDescendingSmall />}
                onClick={handleSwitchToLastModifiedSort}
                dataHook="context-menu.sort-switcher.last-modified"
              />
              <ContextMenuItem
                title={<FormattedMessage id="sort-switcher.oldest-first" />}
                icon={<WixIcons.SortAscendingSmall />}
                onClick={handleSwitchToOldestFirstSort}
                dataHook="context-menu.sort-switcher.oldest-first"
              />
              <ContextMenuItem
                title={<FormattedMessage id="sort-switcher.az" />}
                icon={<WixIcons.SortAZSmall />}
                onClick={handleSwitchToAZSort}
                dataHook="context-menu.sort-switcher.az"
              />
              <ContextMenuItem
                title={<FormattedMessage id="sort-switcher.za" />}
                icon={<WixIcons.SortZASmall />}
                onClick={handleSwitchToZASort}
                dataHook="context-menu.sort-switcher.za"
              />
            </SubMenu>

            {isTrash && (
              <>
                <ContextMenuItem divider />
                <ContextMenuItem
                  title={<FormattedMessage id="actions.empty-trash" />}
                  onClick={handleEmptyTrashClick}
                  dataHook="context-menu.empty-trash"
                />
              </>
            )}
          </>
        )}
      </ContextMenu>
    </>
  );
};
